'use client'

import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, MapPin, Phone, Video } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './sheet'
import { Skeleton } from './skeleton'
import { WIDGET_SHOW_MORE_CLASS, WidgetShell } from './widget-shell'

// ─── Wire shape (HQ `GET /api/my-events`) ────────────────────────────────────

export type MyEventsKind = 'online' | 'in_person' | 'phone' | 'none'

export interface MyEventsWho {
  main: string
  /** The count part ("+4 orgs · 12 people"); never truncated. */
  more: string
}

export interface MyEventsGroup {
  label: string
  kind: 'org' | 'individuals' | 'ordo' | 'declined'
  people: Array<{ name: string; detail: string | null }>
}

export interface MyEventsScheduleInfo {
  leaveBy: string | null
  arriveBy: string | null
  driveMinutes: number | null
  role: string | null
  crew: string[]
  city: string | null
  href: string
}

export interface MyEventsEvent {
  id: string
  source: 'google' | 'schedule'
  title: string
  start: string
  end: string
  kind: MyEventsKind
  joinUrl: string | null
  place: string | null
  address: string | null
  who: MyEventsWho
  groups: MyEventsGroup[]
  conflict: string | null
  href: string | null
  schedule: MyEventsScheduleInfo | null
}

export interface MyEventsDay {
  date: string
  timeZone: string
  calendar: 'ok' | 'not_connected' | 'unavailable'
  allDay: string[]
  events: MyEventsEvent[]
  next: { title: string; start: string } | null
  /** Google Calendar's week view: Show more on desktop. */
  calendarUrl: string
  /** Google Calendar's day view: Show more on a phone, where a week is too wide to read. */
  calendarDayUrl?: string
}

export interface MyEventsWidgetProps {
  /** Null while loading: the card shows a skeleton of the strip and rows. */
  day: MyEventsDay | null
  /** "Today", "Tomorrow", "Mon, Oct 19". */
  dayLabel?: string
  onPreviousDay?: () => void
  onNextDay?: () => void
  /** Injected for tests and stories; the card keeps its own clock otherwise. */
  now?: Date
  scheduleHref?: string
}

// Schedule (Road Warrior) visits are drawn in orange, the Schedule app's own color, which has no
// theme token; everything else uses the shared tokens (meetings = primary blue, now = destructive,
// overlaps = warning).

// ─── Pure helpers (exported for tests) ───────────────────────────────────────

const MINUTE = 60_000
/** Join turns on this long before a call. */
export const JOIN_LEAD_MS = 10 * MINUTE

function parts(iso: string, timeZone: string) {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(iso))
  const n = (t: string) => Number(p.find((x) => x.type === t)?.value ?? 0)
  return {
    date: `${n('year')}-${String(n('month')).padStart(2, '0')}-${String(n('day')).padStart(2, '0')}`,
    minutes: n('hour') * 60 + n('minute'),
  }
}

/** "9:30 AM" in the viewer's calendar time zone. */
export function formatClock(iso: string, timeZone: string, withPeriod = true): string {
  const text = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(iso))
  return withPeriod ? text : text.replace(/\s?[AP]M$/, '')
}

/** "8 min", "1h 5m", "2 hr". */
export function formatSpan(ms: number): string {
  const m = Math.max(0, Math.round(ms / MINUTE))
  if (m < 60) {
    return `${m} min`
  }
  return m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60} hr`
}

/** Events still on or ahead; the list never shows the past. */
export function upcomingEvents(events: MyEventsEvent[], now: Date): MyEventsEvent[] {
  return events.filter((e) => new Date(e.end).getTime() > now.getTime())
}

/** Join shows only for a call that is live or starts within ten minutes. */
export function isJoinable(e: MyEventsEvent, now: Date): boolean {
  if (e.kind !== 'online' || !e.joinUrl) {
    return false
  }
  const start = new Date(e.start).getTime()
  const end = new Date(e.end).getTime()
  return now.getTime() < end && start - now.getTime() <= JOIN_LEAD_MS
}

const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' })
const SHORT_DAY = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000)
}

/** "tomorrow", "Monday", or "Mon, Oct 26" for the "Next:" line, relative to the day shown. */
export function nextDayPhrase(nextStart: string, day: Pick<MyEventsDay, 'date' | 'timeZone'>): string {
  const local = parts(nextStart, day.timeZone).date
  const gap = daysBetween(day.date, local)
  const asUtc = new Date(`${local}T12:00:00Z`)
  if (gap === 1) {
    return 'tomorrow'
  }
  return gap > 1 && gap < 7 ? WEEKDAY.format(asUtc) : SHORT_DAY.format(asUtc)
}

/** The strip's span: 8a–6p, stretched for anything earlier or later. Minutes since local midnight. */
export function stripBounds(events: MyEventsEvent[], timeZone: string): { lo: number; hi: number } {
  let lo = 8 * 60
  let hi = 18 * 60
  for (const e of events) {
    const lead = e.schedule?.leaveBy ?? e.start
    lo = Math.min(lo, Math.floor(parts(lead, timeZone).minutes / 60) * 60)
    const end = parts(e.end, timeZone)
    hi = Math.max(hi, Math.ceil((end.minutes || 24 * 60) / 60) * 60)
  }
  return { lo, hi: Math.min(hi, 24 * 60) }
}

function whereText(e: MyEventsEvent): string {
  if (e.kind === 'online') {
    const host = e.joinUrl ? new URL(e.joinUrl).hostname : ''
    const app = host.includes('zoom.us') ? 'Zoom' : host.includes('teams.microsoft.com') ? 'Teams' : 'Google Meet'
    return e.place ? `${app} · ${e.place}` : app
  }
  if (e.kind === 'in_person') {
    return e.address ?? e.place ?? 'In person'
  }
  return e.kind === 'phone' ? 'Phone' : ''
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

function useNow(injected?: Date): Date {
  const [now, setNow] = React.useState(() => injected ?? new Date())
  React.useEffect(() => {
    if (injected) {
      setNow(injected)
      return
    }
    const id = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(id)
  }, [injected])
  return now
}

const HOVER_QUERY = '(hover: hover) and (pointer: fine)'

/** True on a mouse-and-hover device; phones get the full-screen sheet instead of popovers. */
function useCanHover(): boolean {
  return React.useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia(HOVER_QUERY)
      mq.addEventListener('change', cb)
      return () => mq.removeEventListener('change', cb)
    },
    () => window.matchMedia(HOVER_QUERY).matches,
    () => true,
  )
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function KindIcon({ e }: { e: MyEventsEvent }) {
  const cls = cn(
    'h-3 w-3 shrink-0',
    e.source === 'schedule' ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground/70',
  )
  if (e.source === 'schedule' || e.kind === 'in_person') {
    return <MapPin className={cls} aria-label="In person" />
  }
  if (e.kind === 'online') {
    return <Video className={cls} aria-label="Online" />
  }
  if (e.kind === 'phone') {
    return <Phone className={cls} aria-label="Phone" />
  }
  return null
}

/**
 * Click (or Enter) opens the detail on desktop. Hover only underlines the text
 * like a link, so scrolling past a row never pops a card open.
 */
function ClickDetail({
  trigger,
  content,
  wide,
  label,
  bare,
}: {
  trigger: React.ReactNode
  content: React.ReactNode
  wide?: boolean
  label: string
  /** `trigger` is already the button (a day-strip block), so the popup anchors to it. */
  bare?: boolean
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        {bare ? (
          trigger
        ) : (
          <button
            type="button"
            aria-label={label}
            className="group block w-full min-w-0 cursor-pointer rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {trigger}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('p-3', wide ? 'w-[38rem]' : 'w-80')}
        onOpenAutoFocus={(ev) => ev.preventDefault()}>
        {content}
      </PopoverContent>
    </Popover>
  )
}

const JOIN_CLASS =
  'inline-flex h-7 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90'

const isWide = (e: MyEventsEvent) => e.groups.filter((g) => g.kind === 'org' || g.kind === 'individuals').length >= 4

/** The full detail: shown in the desktop popover and the phone sheet. */
export function EventDetail({
  e,
  timeZone,
  showTitle = true,
}: {
  e: MyEventsEvent
  timeZone: string
  showTitle?: boolean
}) {
  const where = whereText(e)
  return (
    <div className="flex flex-col gap-2 text-sm">
      <div>
        {showTitle ? <p className="font-semibold leading-tight">{e.title}</p> : null}
        <p className="text-xs text-muted-foreground">
          {formatClock(e.start, timeZone)} – {formatClock(e.end, timeZone)}
          {where ? ` · ${where}` : ''}
        </p>
      </div>
      {e.kind === 'online' && e.joinUrl ? (
        // Always here, whatever the time: the row's Join waits for ten minutes out, the detail never does.
        <a href={e.joinUrl} target="_blank" rel="noopener noreferrer" className={cn(JOIN_CLASS, 'self-start')}>
          <Video className="h-3.5 w-3.5" />
          Join {whereText({ ...e, place: null })}
        </a>
      ) : null}
      {e.schedule ? (
        <dl className="grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-1 border-t border-border pt-2">
          {e.address ? (
            <>
              <dt className="text-muted-foreground">Address</dt>
              <dd>{e.address}</dd>
            </>
          ) : null}
          {e.schedule.leaveBy ? (
            <>
              <dt className="text-muted-foreground">Leave by</dt>
              <dd>
                {formatClock(e.schedule.leaveBy, timeZone)}
                {e.schedule.driveMinutes ? ` · ${e.schedule.driveMinutes} min drive` : ''}
              </dd>
            </>
          ) : null}
          {e.schedule.arriveBy ? (
            <>
              <dt className="text-muted-foreground">Arrive by</dt>
              <dd>{formatClock(e.schedule.arriveBy, timeZone)}</dd>
            </>
          ) : null}
          {e.schedule.role ? (
            <>
              <dt className="text-muted-foreground">Your role</dt>
              <dd>{e.schedule.role}</dd>
            </>
          ) : null}
          {e.schedule.crew.length > 0 ? (
            <>
              <dt className="text-muted-foreground">Also going</dt>
              <dd>{e.schedule.crew.join(', ')}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {e.groups.length > 0 ? (
        <div className={cn('grid gap-x-6 gap-y-2', isWide(e) && 'sm:grid-cols-2')}>
          {e.groups.map((g) => (
            <div key={`${g.kind}:${g.label}`} className="flex flex-col gap-0.5 border-t border-border pt-2">
              <div className="flex justify-between text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span className="truncate">{g.label}</span>
                {g.kind === 'ordo' ? null : <span className="tabular-nums">{g.people.length}</span>}
              </div>
              {g.people.map((p, i) => (
                <div key={`${p.name}:${i}`} className="flex min-w-0 gap-1.5">
                  <span
                    className={cn(
                      'whitespace-nowrap font-medium',
                      g.kind === 'declined' && 'font-normal text-muted-foreground line-through',
                    )}>
                    {p.name}
                  </span>
                  {p.detail ? <span className="truncate text-muted-foreground">{p.detail}</span> : null}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function WhoLine({ e, now }: { e: MyEventsEvent; now: Date }) {
  let main = e.who.main
  if (e.schedule) {
    const leave = e.schedule.leaveBy ? new Date(e.schedule.leaveBy).getTime() : null
    const started = new Date(e.start).getTime() <= now.getTime()
    const tail = started
      ? 'At the school'
      : leave && leave > now.getTime()
        ? leave - now.getTime() <= 60 * MINUTE
          ? `Leave in ${formatSpan(leave - now.getTime())}`
          : null
        : leave
          ? 'On the way'
          : null
    main = [e.schedule.city ?? e.who.main, tail].filter(Boolean).join(' · ')
  }
  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-1 text-xs text-muted-foreground',
        e.source === 'schedule' && 'text-orange-700 dark:text-orange-300',
      )}>
      <KindIcon e={e} />
      <span className="truncate">{main}</span>
      {e.who.more ? (
        <span className="shrink-0 whitespace-pre underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
          {e.who.more}
        </span>
      ) : null}
      {e.source === 'google' && e.place ? <span className="truncate">{` · ${e.place}`}</span> : null}
    </span>
  )
}

function Row({
  e,
  now,
  timeZone,
  canHover,
  onOpen,
}: {
  e: MyEventsEvent
  now: Date
  timeZone: string
  canHover: boolean
  onOpen: (e: MyEventsEvent) => void
}) {
  const live = new Date(e.start).getTime() <= now.getTime()
  const until = new Date(e.start).getTime() - now.getTime()
  const join = isJoinable(e, now) && e.joinUrl
  const text = (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-sm font-semibold decoration-foreground/40 underline-offset-2 group-hover:underline">
          {e.title}
        </span>
        {e.conflict ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-warning-border bg-warning px-1.5 py-px text-[11px] font-medium text-warning-foreground">
            <AlertTriangle className="h-3 w-3" />
            {e.conflict}
          </span>
        ) : null}
      </span>
      <WhoLine e={e} now={now} />
    </>
  )
  return (
    <li
      className={cn(
        'grid h-12 grid-cols-[4rem_1rem_minmax(0,1fr)_auto] items-center gap-x-2 px-4',
        e.source === 'schedule' && 'bg-orange-50 dark:bg-orange-950/30',
        !canHover && 'cursor-pointer active:bg-muted/60',
      )}
      onClick={(ev) => {
        if (!canHover && !(ev.target as HTMLElement).closest('a')) {
          onOpen(e)
        }
      }}>
      <div className={cn('text-right text-xs font-semibold leading-tight tabular-nums', live && 'text-destructive')}>
        {live ? 'Now' : formatClock(e.start, timeZone)}
        <span className="block text-[11px] font-normal text-muted-foreground">
          {live
            ? `until ${formatClock(e.end, timeZone, false)}`
            : until <= 60 * MINUTE
              ? `in ${formatSpan(until)}`
              : ' '}
        </span>
      </div>
      <div className="relative flex items-center justify-center self-stretch" aria-hidden="true">
        <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" />
        <span
          className={cn(
            'relative h-2.5 w-2.5 rounded-full border-2 bg-card',
            live
              ? 'border-destructive bg-destructive ring-[3px] ring-destructive/20'
              : e.source === 'schedule'
                ? 'border-orange-500 bg-orange-500'
                : 'border-primary bg-primary',
          )}
        />
      </div>
      <div className="min-w-0">
        {canHover ? (
          <ClickDetail
            label={`${e.title}: details`}
            wide={isWide(e)}
            content={<EventDetail e={e} timeZone={timeZone} />}
            trigger={text}
          />
        ) : (
          text
        )}
      </div>
      <div>
        {join ? (
          <a href={e.joinUrl!} target="_blank" rel="noopener noreferrer" className={JOIN_CLASS}>
            <Video className="h-3.5 w-3.5" />
            Join
          </a>
        ) : null}
      </div>
    </li>
  )
}

function DayStrip({ day, now, canHover }: { day: MyEventsDay; now: Date; canHover: boolean }) {
  const { lo, hi } = stripBounds(day.events, day.timeZone)
  const pct = (iso: string) => ((Math.min(Math.max(parts(iso, day.timeZone).minutes, lo), hi) - lo) / (hi - lo)) * 100
  const nowParts = parts(now.toISOString(), day.timeZone)
  const showNow = nowParts.date === day.date && nowParts.minutes >= lo && nowParts.minutes <= hi
  const hours: number[] = []
  for (let h = lo / 60 + 1; h < hi / 60; h++) {
    hours.push(h)
  }
  const labels = [8, 10, 12, 14, 16, 18].filter((h) => h * 60 >= lo && h * 60 <= hi)
  const label = (h: number) => `${((h + 11) % 12) + 1}${h < 12 ? 'a' : 'p'}`
  return (
    <div className="hidden shrink-0 border-b border-border px-4 pb-1.5 pt-3 sm:block">
      <div className="relative h-[26px] rounded-md bg-muted">
        {hours.map((h) => (
          <span
            key={h}
            className="absolute inset-y-0 w-px bg-border"
            style={{ left: `${((h * 60 - lo) / (hi - lo)) * 100}%` }}
          />
        ))}
        {day.events.map((e) => {
          const past = new Date(e.end).getTime() <= now.getTime()
          const left = pct(e.start)
          const width = Math.max(pct(e.end) - left, 0.8)
          const block = (
            <button
              type="button"
              aria-label={`${e.title}: details`}
              className={cn(
                'absolute flex cursor-pointer items-center overflow-hidden whitespace-nowrap rounded px-1 text-[10px] font-semibold outline-none transition-[filter] hover:brightness-110 focus-visible:ring-2 focus-visible:ring-ring',
                e.source === 'schedule'
                  ? 'inset-y-0 z-[2] bg-orange-500 text-white'
                  : // Meetings use the page's blue (Join, the sales bars), like Google Calendar.
                    'inset-y-[3px] bg-primary text-primary-foreground',
                e.conflict &&
                  'inset-y-auto bottom-0 top-1/2 z-[3] border-warning-border bg-warning text-warning-foreground',
                past && 'opacity-40',
              )}
              style={{ left: `${left}%`, width: `calc(${width}% - 2px)` }}>
              {e.title}
            </button>
          )
          return (
            <React.Fragment key={e.id}>
              {e.schedule?.leaveBy ? (
                <span
                  className={cn(
                    'absolute inset-y-0 rounded border border-dashed border-orange-300 bg-[repeating-linear-gradient(135deg,transparent_0_4px,rgb(253_186_116/0.5)_4px_6px)]',
                    past && 'opacity-40',
                  )}
                  style={{
                    left: `${pct(e.schedule.leaveBy)}%`,
                    width: `${Math.max(left - pct(e.schedule.leaveBy), 0)}%`,
                  }}
                />
              ) : null}
              {canHover ? (
                <ClickDetail
                  bare
                  label={`${e.title}: details`}
                  wide={isWide(e)}
                  content={<EventDetail e={e} timeZone={day.timeZone} />}
                  trigger={block}
                />
              ) : (
                block
              )}
            </React.Fragment>
          )
        })}
        {showNow ? (
          <span
            className="absolute -inset-y-1 z-[4] w-0.5 -translate-x-1/2 rounded bg-destructive"
            style={{ left: `${((nowParts.minutes - lo) / (hi - lo)) * 100}%` }}>
            <span className="absolute -left-[3px] -top-[3px] h-2 w-2 rounded-full bg-destructive" />
          </span>
        ) : null}
      </div>
      <div className="relative mt-1 h-3.5 text-[10px] text-muted-foreground">
        {labels.map((h) => (
          <span key={h} className="absolute -translate-x-1/2" style={{ left: `${((h * 60 - lo) / (hi - lo)) * 100}%` }}>
            {label(h)}
          </span>
        ))}
      </div>
    </div>
  )
}

function NextLine({ day }: { day: MyEventsDay }) {
  if (!day.next) {
    return null
  }
  return (
    <p className="text-sm text-muted-foreground">
      Next: <span className="font-semibold text-foreground">{day.next.title}</span>,{' '}
      {nextDayPhrase(day.next.start, day)} at {formatClock(day.next.start, day.timeZone)}
    </p>
  )
}

function CenteredState({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1.5 px-6 text-center">
      <Calendar className="mb-1 h-6 w-6 text-muted-foreground/60" />
      <p className="text-sm font-semibold">{title}</p>
      {children}
    </div>
  )
}

function LoadingBody() {
  return (
    <div className="flex h-full flex-col" aria-busy="true">
      <div className="hidden border-b border-border px-4 pb-3 pt-3 sm:block">
        <Skeleton className="h-[26px] w-full" />
      </div>
      <ul className="divide-y divide-transparent py-1">
        {[0, 1, 2].map((i) => (
          <li key={i} className="grid h-12 grid-cols-[4rem_1rem_minmax(0,1fr)] items-center gap-x-2 px-4">
            <Skeleton className="ml-auto h-3 w-12" />
            <Skeleton className="mx-auto h-2.5 w-2.5 rounded-full" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Widget ──────────────────────────────────────────────────────────────────

/**
 * My Events: the viewer's day. A to-scale day strip (desktop only), then what's
 * on now and what's next as same-size two-line rows — the title, then who it's
 * with. Join is the only action. Hover opens the detail on desktop; on a phone
 * a tap opens it full screen. Fixed height, like My Tasks: "Show more" opens
 * Google Calendar — the week on desktop, the day on a phone.
 */
export function MyEventsWidget({
  day,
  dayLabel = 'Today',
  onPreviousDay,
  onNextDay,
  now: injectedNow,
  scheduleHref,
}: MyEventsWidgetProps) {
  const now = useNow(injectedNow)
  const canHover = useCanHover()
  const [selected, setSelected] = React.useState<MyEventsEvent | null>(null)

  const stepper = (
    <div className="ml-1 flex items-center gap-1">
      <button
        type="button"
        aria-label="Previous day"
        onClick={onPreviousDay}
        disabled={!onPreviousDay}
        className="grid h-7 w-7 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="min-w-[4.5rem] text-center text-sm font-medium">{dayLabel}</span>
      <button
        type="button"
        aria-label="Next day"
        onClick={onNextDay}
        disabled={!onNextDay}
        className="grid h-7 w-7 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )

  const actions = [
    ...(day ? [{ label: 'Open Google Calendar', href: day.calendarUrl, icon: Calendar }] : []),
    ...(scheduleHref ? [{ label: 'Open Schedule', href: scheduleHref, icon: MapPin }] : []),
  ]
  // Desktop opens the week; a phone opens the day (same 640px break that hides the strip).
  const showMore = day ? (
    <>
      <a
        href={day.calendarUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(WIDGET_SHOW_MORE_CLASS, 'hidden sm:block')}>
        Show more
      </a>
      <a
        href={day.calendarDayUrl ?? day.calendarUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(WIDGET_SHOW_MORE_CLASS, 'sm:hidden')}>
        Show more
      </a>
    </>
  ) : undefined

  let body: React.ReactNode
  if (!day) {
    body = <LoadingBody />
  } else if (day.events.length === 0 && day.calendar === 'not_connected') {
    body = (
      <CenteredState title="Google Calendar isn’t connected yet">
        <p className="max-w-[34ch] text-sm text-muted-foreground">
          Your meetings will show here once Google Calendar is connected for your account.
        </p>
      </CenteredState>
    )
  } else if (day.events.length === 0) {
    body = (
      <CenteredState
        title={
          dayLabel === 'Today'
            ? 'Nothing on your calendar today'
            : `Nothing on your calendar ${dayLabel.toLowerCase() === 'tomorrow' ? 'tomorrow' : `on ${dayLabel}`}`
        }>
        <NextLine day={day} />
      </CenteredState>
    )
  } else {
    const upcoming = upcomingEvents(day.events, now)
    const rows = upcoming
    body = (
      <div className="flex h-full flex-col">
        {day.allDay.length > 0 ? (
          <div className="flex shrink-0 items-center gap-1.5 overflow-hidden px-4 pt-2.5 text-xs text-muted-foreground">
            <span>All day</span>
            {day.allDay.map((t, i) => (
              <span
                key={`${t}:${i}`}
                className="truncate rounded-full bg-muted px-2 py-0.5 font-medium text-foreground/80">
                {t}
              </span>
            ))}
          </div>
        ) : null}
        <DayStrip day={day} now={now} canHover={canHover} />
        {rows.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-1 px-6 text-center">
            <p className="text-sm font-semibold">That’s everything for today</p>
            <NextLine day={day} />
          </div>
        ) : (
          // Scrolls inside on desktop. A phone shows five whole rows and no inner scroll, which would fight the page scroll.
          <ul className="min-h-0 flex-1 overflow-hidden py-1 max-sm:[&>li:nth-child(n+6)]:hidden sm:overflow-x-hidden sm:overflow-y-auto sm:overscroll-contain">
            {rows.map((e) => (
              <Row key={e.id} e={e} now={now} timeZone={day.timeZone} canHover={canHover} onOpen={setSelected} />
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <>
      <WidgetShell title="My Events" headerExtra={stepper} actions={actions} showMore={showMore}>
        {body}
      </WidgetShell>
      {day ? (
        <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
          <SheetContent side="bottom" size="full" className="overflow-y-auto">
            {selected ? (
              <>
                <SheetHeader className="pr-10 text-left">
                  <SheetTitle>{selected.title}</SheetTitle>
                </SheetHeader>
                <div className="mt-3">
                  <EventDetail e={selected} timeZone={day.timeZone} showTitle={false} />
                </div>
              </>
            ) : null}
          </SheetContent>
        </Sheet>
      ) : null}
    </>
  )
}
